import { useEffect, useState } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import "./Toast.css";
import { translate } from "../i18n/translate";

export type ToastType = "success" | "error" | "info" | "action";

// Default durations for each toast type (in ms)
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  error: 4000,
  info: 4000,
  action: 10000, // Long duration - dismissed manually when action completes
};

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
  icon?: string; // Custom icon/emoji (overrides default)
  onClose: () => void;
}

export function Toast({ message, type, duration, icon, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [copied, setCopied] = useState(false);
  const effectiveDuration = duration ?? DEFAULT_DURATIONS[type];

  const handleCopy = async () => {
    // Include page + time so a pasted error is self-describing to a developer.
    const page = typeof window !== "undefined" ? window.location.href : "n/a";
    const payload = `Suikamon error\nTime: ${new Date().toISOString()}\nPage: ${page}\n\n${message}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = payload;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(translate("navigation.toast.copyPrompt"), payload);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, effectiveDuration);

    return () => clearTimeout(timer);
  }, [effectiveDuration, onClose]);

  const getDefaultIcon = () => {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "info":
        return "ⓘ";
      case "action":
        return "⚡";
    }
  };

  // Use custom icon if provided, otherwise use default
  const displayIcon = icon ?? getDefaultIcon();

  const getColor = () => {
    switch (type) {
      case "success":
        return "#4ade80";
      case "error":
        return "#ff6b6b";
      case "info":
        return "#60a5fa";
      case "action":
        return "#fbbf24";
    }
  };

  return (
    <Box
      className={`suikamon-toast suikamon-toast--${type} ${isVisible ? "suikamon-toast--visible" : "suikamon-toast--hidden"}`}
      style={{
        borderLeftColor: getColor(),
      }}
    >
      <Flex align="center" gap="3">
        {/* Action toasts show emoji without background, others get colored icon box */}
        {type === "action" ? (
          <Text size="5" style={{ lineHeight: 1 }}>
            {displayIcon}
          </Text>
        ) : (
          <Box
            className="suikamon-toast__icon"
            style={{
              background: getColor(),
            }}
          >
            <Text size="4" weight="bold" style={{ color: "#fff" }}>
              {displayIcon}
            </Text>
          </Box>
        )}
        <Text size="3" weight="medium" style={{ color: "#fff", flex: 1 }}>
          {message}
        </Text>
        {type === "error" && (
          <button
            onClick={handleCopy}
            className="suikamon-toast__copy"
            aria-label={translate("navigation.toast.copy")}
            title={translate("navigation.toast.copy")}
          >
            {copied ? "✓" : "⧉"}
          </button>
        )}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="suikamon-toast__close"
          aria-label={translate("common.action.close")}
        >
          ✕
        </button>
      </Flex>
    </Box>
  );
}
